import { supabase } from '../lib/supabase';

/**
 * Analytics Service - Handles all analytics data operations
 * Converts between snake_case (DB) and camelCase (React)
 */

export const analyticsService = {
  /**
   * Get dashboard metrics for a company
   */
  async getDashboardMetrics(companyId, dateRange = 'today') {
    try {
      const { data, error } = await supabase?.from('analytics_metrics')?.select('*')?.eq('company_id', companyId)?.gte('metric_date', this._getDateRangeStart(dateRange))?.order('metric_date', { ascending: false });

      if (error) throw error;

      return data?.map(metric => ({
        id: metric?.id,
        companyId: metric?.company_id,
        metricDate: metric?.metric_date,
        totalSessions: metric?.total_sessions,
        uniqueVisitors: metric?.unique_visitors,
        completedSessions: metric?.completed_sessions,
        abandonedSessions: metric?.abandoned_sessions,
        averageDurationSeconds: metric?.average_duration_seconds,
        totalConversions: metric?.total_conversions,
        conversionRate: metric?.conversion_rate,
        desktopSessions: metric?.desktop_sessions,
        mobileSessions: metric?.mobile_sessions,
        tabletSessions: metric?.tablet_sessions,
        createdAt: metric?.created_at,
        updatedAt: metric?.updated_at
      })) || [];
    } catch (err) {
      throw new Error(`Failed to fetch dashboard metrics: ${err.message}`);
    }
  },

  /**
   * Get visualizer sessions with optional filtering
   */
  async getVisualizerSessions(companyId, filters = {}) {
    try {
      let query = supabase?.from('visualizer_sessions')?.select('*')?.eq('company_id', companyId)?.order('started_at', { ascending: false });

      if (filters?.status) {
        query = query?.eq('session_status', filters?.status);
      }

      if (filters?.deviceType) {
        query = query?.eq('device_type', filters?.deviceType);
      }

      if (filters?.startDate) {
        query = query?.gte('started_at', filters?.startDate);
      }

      const { data, error } = await query;

      if (error) throw error;

      return data?.map(session => ({
        id: session?.id,
        companyId: session?.company_id,
        sessionId: session?.session_id,
        visitorId: session?.visitor_id,
        deviceType: session?.device_type,
        trafficSource: session?.traffic_source,
        referrerUrl: session?.referrer_url,
        startedAt: session?.started_at,
        completedAt: session?.completed_at,
        sessionStatus: session?.session_status,
        durationSeconds: session?.duration_seconds,
        pageViews: session?.page_views,
        createdAt: session?.created_at
      })) || [];
    } catch (err) {
      throw new Error(`Failed to fetch visualizer sessions: ${err.message}`);
    }
  },

  /**
   * Get project conversions with funnel data
   */
  async getProjectConversions(companyId, filters = {}) {
    try {
      let query = supabase?.from('project_conversions')?.select(`
          *,
          visualizer_sessions:session_id (
            session_id,
            visitor_id,
            device_type,
            traffic_source
          )
        `)?.eq('company_id', companyId)?.order('created_at', { ascending: false });

      if (filters?.hasReferenceCode !== undefined) {
        if (filters?.hasReferenceCode) {
          query = query?.not('reference_code', 'is', null);
        } else {
          query = query?.is('reference_code', null);
        }
      }

      const { data, error } = await query;

      if (error) throw error;

      return data?.map(conversion => ({
        id: conversion?.id,
        sessionId: conversion?.session_id,
        companyId: conversion?.company_id,
        floorTypeSelected: conversion?.floor_type_selected,
        colorSelected: conversion?.color_selected,
        roomSelected: conversion?.room_selected,
        previewViewed: conversion?.preview_viewed,
        referenceCodeGenerated: conversion?.reference_code_generated,
        referenceCode: conversion?.reference_code,
        conversionRate: conversion?.conversion_rate,
        completedAt: conversion?.completed_at,
        sessionData: conversion?.visualizer_sessions,
        createdAt: conversion?.created_at
      })) || [];
    } catch (err) {
      throw new Error(`Failed to fetch project conversions: ${err.message}`);
    }
  },

  /**
   * Get conversion funnel analytics
   */
  async getConversionFunnel(companyId, dateRange = 'today') {
    try {
      const { data, error } = await supabase?.from('project_conversions')?.select('*')?.eq('company_id', companyId)?.gte('created_at', this._getDateRangeStart(dateRange));

      if (error) throw error;

      const total = data?.length || 0;
      const floorTypeCount = data?.filter(c => c?.floor_type_selected)?.length || 0;
      const colorCount = data?.filter(c => c?.color_selected)?.length || 0;
      const roomCount = data?.filter(c => c?.room_selected)?.length || 0;
      const previewCount = data?.filter(c => c?.preview_viewed)?.length || 0;
      const referenceCodeCount = data?.filter(c => c?.reference_code_generated)?.length || 0;

      return [
        { step: 'Started', count: total, percentage: 100 },
        { step: 'Floor Type', count: floorTypeCount, percentage: total ? (floorTypeCount / total * 100)?.toFixed(1) : 0 },
        { step: 'Color', count: colorCount, percentage: total ? (colorCount / total * 100)?.toFixed(1) : 0 },
        { step: 'Room', count: roomCount, percentage: total ? (roomCount / total * 100)?.toFixed(1) : 0 },
        { step: 'Preview', count: previewCount, percentage: total ? (previewCount / total * 100)?.toFixed(1) : 0 },
        { step: 'Reference Code', count: referenceCodeCount, percentage: total ? (referenceCodeCount / total * 100)?.toFixed(1) : 0 }
      ];
    } catch (err) {
      throw new Error(`Failed to fetch conversion funnel: ${err.message}`);
    }
  },

  /**
   * Get staff activity logs
   */
  async getStaffActivity(companyId, limit = 50) {
    try {
      const { data, error } = await supabase?.from('staff_activity_log')?.select(`
          *,
          user_profiles:user_id (
            full_name,
            email
          )
        `)?.eq('company_id', companyId)?.order('created_at', { ascending: false })?.limit(limit);

      if (error) throw error;

      return data?.map(activity => ({
        id: activity?.id,
        userId: activity?.user_id,
        companyId: activity?.company_id,
        actionType: activity?.action_type,
        actionDescription: activity?.action_description,
        metadata: activity?.metadata,
        userFullName: activity?.user_profiles?.full_name,
        userEmail: activity?.user_profiles?.email,
        createdAt: activity?.created_at
      })) || [];
    } catch (err) {
      throw new Error(`Failed to fetch staff activity: ${err.message}`);
    }
  },

  /**
   * Log staff activity
   */
  async logStaffActivity(userId, companyId, actionType, actionDescription, metadata = {}) {
    try {
      const { error } = await supabase?.from('staff_activity_log')?.insert({
          user_id: userId,
          company_id: companyId,
          action_type: actionType,
          action_description: actionDescription,
          metadata: metadata
        });

      if (error) throw error;
    } catch (err) {
      throw new Error(`Failed to log staff activity: ${err.message}`);
    }
  },

  /**
   * Get trend data for charts (last 7 days)
   */
  async getTrendData(companyId, metric = 'sessions', days = 7) {
    try {
      const startDate = new Date();
      startDate?.setDate(startDate?.getDate() - days);

      const { data, error } = await supabase?.from('analytics_metrics')?.select('metric_date, total_sessions, unique_visitors, total_conversions, conversion_rate')?.eq('company_id', companyId)?.gte('metric_date', startDate?.toISOString()?.split('T')?.[0])?.order('metric_date', { ascending: true });

      if (error) throw error;

      return data?.map(item => ({
        date: new Date(item.metric_date)?.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
        sessions: item?.total_sessions,
        visitors: item?.unique_visitors,
        conversions: item?.total_conversions,
        conversionRate: item?.conversion_rate
      })) || [];
    } catch (err) {
      throw new Error(`Failed to fetch trend data: ${err.message}`);
    }
  },

  /**
   * Subscribe to real-time analytics metrics updates
   * @param {string} companyId - The company ID to monitor
   * @param {function} onUpdate - Callback function to handle updates
   * @returns {object} Subscription object with unsubscribe method
   */
  subscribeToMetricsUpdates(companyId, onUpdate) {
    const channel = supabase
      ?.channel(`analytics_metrics_${companyId}`)
      ?.on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'analytics_metrics',
          filter: `company_id=eq.${companyId}`
        },
        (payload) => {
          const formattedData = {
            id: payload?.new?.id,
            companyId: payload?.new?.company_id,
            metricDate: payload?.new?.metric_date,
            totalSessions: payload?.new?.total_sessions,
            uniqueVisitors: payload?.new?.unique_visitors,
            completedSessions: payload?.new?.completed_sessions,
            abandonedSessions: payload?.new?.abandoned_sessions,
            averageDurationSeconds: payload?.new?.average_duration_seconds,
            totalConversions: payload?.new?.total_conversions,
            conversionRate: payload?.new?.conversion_rate,
            desktopSessions: payload?.new?.desktop_sessions,
            mobileSessions: payload?.new?.mobile_sessions,
            tabletSessions: payload?.new?.tablet_sessions,
            createdAt: payload?.new?.created_at,
            updatedAt: payload?.new?.updated_at
          };
          onUpdate?.(formattedData, payload?.eventType);
        }
      )
      ?.subscribe();

    return {
      unsubscribe: () => {
        supabase?.removeChannel(channel);
      }
    };
  },

  /**
   * Subscribe to real-time visualizer session updates
   * @param {string} companyId - The company ID to monitor
   * @param {function} onUpdate - Callback function to handle updates
   * @returns {object} Subscription object with unsubscribe method
   */
  subscribeToSessionUpdates(companyId, onUpdate) {
    const channel = supabase
      ?.channel(`visualizer_sessions_${companyId}`)
      ?.on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'visualizer_sessions',
          filter: `company_id=eq.${companyId}`
        },
        (payload) => {
          const formattedData = {
            id: payload?.new?.id,
            companyId: payload?.new?.company_id,
            sessionId: payload?.new?.session_id,
            visitorId: payload?.new?.visitor_id,
            deviceType: payload?.new?.device_type,
            trafficSource: payload?.new?.traffic_source,
            referrerUrl: payload?.new?.referrer_url,
            startedAt: payload?.new?.started_at,
            completedAt: payload?.new?.completed_at,
            sessionStatus: payload?.new?.session_status,
            durationSeconds: payload?.new?.duration_seconds,
            pageViews: payload?.new?.page_views,
            createdAt: payload?.new?.created_at
          };
          onUpdate?.(formattedData, payload?.eventType);
        }
      )
      ?.subscribe();

    return {
      unsubscribe: () => {
        supabase?.removeChannel(channel);
      }
    };
  },

  /**
   * Subscribe to real-time project conversion updates
   * @param {string} companyId - The company ID to monitor
   * @param {function} onUpdate - Callback function to handle updates
   * @returns {object} Subscription object with unsubscribe method
   */
  subscribeToConversionUpdates(companyId, onUpdate) {
    const channel = supabase
      ?.channel(`project_conversions_${companyId}`)
      ?.on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'project_conversions',
          filter: `company_id=eq.${companyId}`
        },
        (payload) => {
          const formattedData = {
            id: payload?.new?.id,
            sessionId: payload?.new?.session_id,
            companyId: payload?.new?.company_id,
            floorTypeSelected: payload?.new?.floor_type_selected,
            colorSelected: payload?.new?.color_selected,
            roomSelected: payload?.new?.room_selected,
            previewViewed: payload?.new?.preview_viewed,
            referenceCodeGenerated: payload?.new?.reference_code_generated,
            referenceCode: payload?.new?.reference_code,
            conversionRate: payload?.new?.conversion_rate,
            completedAt: payload?.new?.completed_at,
            createdAt: payload?.new?.created_at
          };
          onUpdate?.(formattedData, payload?.eventType);
        }
      )
      ?.subscribe();

    return {
      unsubscribe: () => {
        supabase?.removeChannel(channel);
      }
    };
  },

  /**
   * Subscribe to real-time staff activity updates
   * @param {string} companyId - The company ID to monitor
   * @param {function} onUpdate - Callback function to handle updates
   * @returns {object} Subscription object with unsubscribe method
   */
  subscribeToStaffActivityUpdates(companyId, onUpdate) {
    const channel = supabase
      ?.channel(`staff_activity_${companyId}`)
      ?.on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'staff_activity_log',
          filter: `company_id=eq.${companyId}`
        },
        async (payload) => {
          // Fetch user profile data for the new activity
          const { data: userProfile } = await supabase
            ?.from('user_profiles')
            ?.select('full_name, email')
            ?.eq('id', payload?.new?.user_id)
            ?.single();

          const formattedData = {
            id: payload?.new?.id,
            userId: payload?.new?.user_id,
            companyId: payload?.new?.company_id,
            actionType: payload?.new?.action_type,
            actionDescription: payload?.new?.action_description,
            metadata: payload?.new?.metadata,
            userFullName: userProfile?.full_name,
            userEmail: userProfile?.email,
            createdAt: payload?.new?.created_at
          };
          onUpdate?.(formattedData);
        }
      )
      ?.subscribe();

    return {
      unsubscribe: () => {
        supabase?.removeChannel(channel);
      }
    };
  },

  /**
   * Helper: Get date range start based on filter
   */
  _getDateRangeStart(range) {
    const now = new Date();
    switch (range) {
      case 'today':
        return now?.toISOString()?.split('T')?.[0];
      case 'week':
        now?.setDate(now?.getDate() - 7);
        return now?.toISOString()?.split('T')?.[0];
      case 'month':
        now?.setMonth(now?.getMonth() - 1);
        return now?.toISOString()?.split('T')?.[0];
      case 'year':
        now?.setFullYear(now?.getFullYear() - 1);
        return now?.toISOString()?.split('T')?.[0];
      default:
        return now?.toISOString()?.split('T')?.[0];
    }
  }
};